import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../hooks/useAuth';
import { useFirebase } from '../../../hooks/useFirebase';
import { queryDelete, queryGet, queryPost, queryPut } from '../../tools/tools';

const ENDPOINT = '/consultants';
const KEY = 'consultants';

export const useConsultants = () => {
  const { user } = useAuth();
  const { remoteConfig } = useFirebase();
  const queryClient = useQueryClient();

  const queryKey = [KEY, user?.uid];

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => queryGet(remoteConfig, ENDPOINT, user?.accessToken),
    enabled: !!user?.accessToken && !!remoteConfig
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const createMutation = useMutation({
    mutationFn: (consultant) => queryPost(remoteConfig, ENDPOINT, consultant, user?.accessToken),
    onSuccess: invalidate
  });

  const updateMutation = useMutation({
    mutationFn: (consultant) => queryPut(remoteConfig, ENDPOINT, consultant, user?.accessToken),
    onSuccess: invalidate
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, removeCoupons = false }) =>
      queryDelete(remoteConfig, ENDPOINT, id, user?.accessToken, { removeCoupons }),
    onSuccess: invalidate
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) =>
      queryPut(remoteConfig, `${ENDPOINT}/${id}/status`, { status }, user?.accessToken),
    onSuccess: invalidate
  });

  const resendWelcomeMutation = useMutation({
    mutationFn: (id) => queryPost(remoteConfig, `${ENDPOINT}/${id}/resend-welcome`, {}, user?.accessToken)
  });

  return {
    consultants: data ?? [],
    isLoading,
    isError,
    refetch,
    create: createMutation.mutate,
    isCreating: createMutation.isPending,
    update: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    remove: deleteMutation.mutate,
    isRemoving: deleteMutation.isPending,
    updateStatus: statusMutation.mutate,
    isUpdatingStatus: statusMutation.isPending,
    resendWelcome: resendWelcomeMutation.mutate,
    isResending: resendWelcomeMutation.isPending
  };
};
